<template>
  <main class="container mx-auto px-4 py-8 max-w-3xl">
    <header class="mb-6">
      <h1 class="text-2xl font-semibold">{{ pageTitle }}</h1>
      <p v-if="shortDescription" class="text-gray-600 mt-2">{{ shortDescription }}</p>
      <div class="text-sm text-gray-500 mt-2">
        <span v-if="channelTitle">Kanal: {{ channelTitle }}</span>
        <span v-if="publishedAt"> • Yayın: {{ new Date(publishedAt).toLocaleString() }}</span>
      </div>
    </header>

    <section v-if="audioUrl" class="mb-6">
      <audio :src="audioUrl" controls class="w-full"></audio>
    </section>

    <section v-if="summaryHtml" class="prose prose-sm sm:prose lg:prose-lg max-w-none">
      <h2>Özet</h2>
      <div v-html="summaryHtml"></div>
    </section>

    <section v-else class="text-gray-600">
      Bu video için özet hazırlanıyor. Lütfen daha sonra tekrar deneyin.
    </section>

    <footer class="mt-10 text-sm text-gray-500">
      <RouterLink :to="{ name: 'public-channel', params: { channelId } }" v-if="channelId" class="underline">Kanal sayfasına dön</RouterLink>
    </footer>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '../services/apiService';

const route = useRoute();
const videoId = ref<string>((route.params.videoId as string) || '');
const slug = ref<string>((route.params.slug as string) || '');
const lang = ref<string>((route.params.lang as string) || 'tr');

// Slug'dan video ID'yi çıkarma fonksiyonu
function extractVideoIdFromSlug(slugParam: string): string {
  // Eğer slug zaten YouTube ID formatındaysa (11 karakter)
  if (slugParam && slugParam.length === 11 && /^[a-zA-Z0-9_-]+$/.test(slugParam)) {
    return slugParam;
  }
  
  // --- ayırıcısından sonraki kısmı al
  const parts = slugParam.split('---');
  if (parts.length >= 2) {
    const videoId = parts[parts.length - 1]; // Son parça
    return videoId;
  }
  
  // Fallback: Eski yöntem (regex ile)
  const youtubeIdPattern = /[a-zA-Z0-9_-]{11}/g;
  const matches = slugParam.match(youtubeIdPattern);
  
  if (matches && matches.length > 0) {
    const youtubeId = matches[matches.length - 1];
    return youtubeId;
  }
  
  return slugParam; // Fallback olarak slug'ı döndür
}

// Video ID'den slug oluşturma fonksiyonu
function createSlugFromTitle(title: string, videoId: string): string {
  if (!title) return videoId;
  
  const slug = title
    .toLowerCase()
    // Türkçe karakterleri İngilizce karşılıklarına çevir
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '') // Diğer özel karakterleri kaldır
    .replace(/\s+/g, '-') // Boşlukları tire ile değiştir
    .replace(/-+/g, '-') // Çoklu tireleri tek tire yap
    .trim();
  
  return `${slug}---${videoId}`;
}

// URL'yi SEO-friendly yapma fonksiyonu
function updateUrlToSeoFriendly(videoId: string, title: string) {
  if (!title || !videoId) return;
  
  const seoSlug = createSlugFromTitle(title, videoId);
  const currentPath = route.path;
  const newPath = `/video/${seoSlug}/${lang.value}`;
  
  // Eğer URL zaten SEO-friendly değilse, güncelle
  if (currentPath !== newPath && !currentPath.includes(seoSlug)) {
    window.history.replaceState({}, '', newPath);
  }
}

const pageTitle = ref<string>('Video Özeti');
const shortDescription = ref<string>('');
const summaryHtml = ref<string>('');
const audioUrl = ref<string>('');
const publishedAt = ref<string>('');
const channelId = ref<string>('');
const channelTitle = ref<string>('');
const availableLanguages = ref<string[]>([]);
const thumbnailUrl = ref<string>('');
const publicBase = (import.meta as any).env?.VITE_PUBLIC_BASE_URL || window.location.origin;

function setMeta(title: string, description: string) {
  document.title = title;
  const metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
  if (metaDesc) {
    metaDesc.setAttribute('content', description);
  } else {
    const m = document.createElement('meta');
    m.setAttribute('name', 'description');
    m.setAttribute('content', description);
    document.head.appendChild(m);
  }
}

function setLinkTag(rel: string, href: string, attrs: Record<string, string> = {}) {
  const selectorParts = [`link[rel="${rel}"]`];
  if (attrs['hreflang']) selectorParts.push(`[hreflang="${attrs['hreflang']}"]`);
  const existing = document.head.querySelector(selectorParts.join('')) as HTMLLinkElement | null;
  const linkTag = existing || document.createElement('link');
  linkTag.setAttribute('rel', rel);
  linkTag.setAttribute('href', href);
  for (const [k, v] of Object.entries(attrs)) linkTag.setAttribute(k, v);
  if (!existing) document.head.appendChild(linkTag);
}

function setMetaTag(attr: 'name' | 'property', key: string, content: string) {
  const existing = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  const tag = existing || document.createElement('meta');
  tag.setAttribute(attr, key);
  tag.setAttribute('content', content);
  if (!existing) document.head.appendChild(tag);
}

function updateSeo() {
  const actualVideoId = slug.value ? extractVideoIdFromSlug(slug.value) : videoId.value;
  const seoSlug = pageTitle.value ? createSlugFromTitle(pageTitle.value, actualVideoId) : actualVideoId;
  const canonicalUrl = `${publicBase}/video/${seoSlug}/${lang.value}`;
  const desc = shortDescription.value || (summaryHtml.value ? summaryHtml.value.replace(/<[^>]+>/g, '').slice(0, 200) : '');

  // Canonical
  setLinkTag('canonical', canonicalUrl);

  // Open Graph
  setMetaTag('property', 'og:title', pageTitle.value);
  setMetaTag('property', 'og:description', desc);
  setMetaTag('property', 'og:type', 'video.other');
  setMetaTag('property', 'og:url', canonicalUrl);
  if (thumbnailUrl.value) setMetaTag('property', 'og:image', thumbnailUrl.value);

  // Twitter
  setMetaTag('name', 'twitter:card', 'summary_large_image');
  setMetaTag('name', 'twitter:title', pageTitle.value);
  setMetaTag('name', 'twitter:description', desc);
  if (thumbnailUrl.value) setMetaTag('name', 'twitter:image', thumbnailUrl.value);

  // hreflang alternates
  // Remove old alternates first
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(n => n.parentElement?.removeChild(n));
  const langs = availableLanguages.value && availableLanguages.value.length ? availableLanguages.value : ['tr', 'en'];
  for (const l of langs) {
    setLinkTag('alternate', `${publicBase}/video/${seoSlug}/${l}`, { hreflang: l });
  }
  // x-default
  setLinkTag('alternate', `${publicBase}/video/${seoSlug}/${langs[0]}`, { hreflang: 'x-default' });
}

function addStructuredData() {
  // Remove existing structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  const actualVideoId = slug.value ? extractVideoIdFromSlug(slug.value) : videoId.value;
  const seoSlug = pageTitle.value ? createSlugFromTitle(pageTitle.value, actualVideoId) : actualVideoId;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": pageTitle.value,
    "description": shortDescription.value || `${pageTitle.value} için ${lang.value.toUpperCase()} kısa özet.`,
    "thumbnailUrl": thumbnailUrl.value || `https://i.ytimg.com/vi/${actualVideoId}/maxresdefault.jpg`,
    "uploadDate": publishedAt.value,
    "publisher": {
      "@type": "Organization",
      "name": "Veciz AI",
      "url": publicBase
    },
    "creator": {
      "@type": "Organization",
      "name": channelTitle.value || "YouTube Channel"
    },
    "url": `${publicBase}/video/${seoSlug}/${lang.value}`,
    "embedUrl": `https://www.youtube.com/embed/${actualVideoId}`,
    "videoId": actualVideoId,
    "inLanguage": lang.value === 'tr' ? 'tr-TR' : 'en-US'
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
}

async function fetchData() {
  // Slug'dan video ID'yi çıkar
  const actualVideoId = slug.value ? extractVideoIdFromSlug(slug.value) : videoId.value;
  
  // Eğer actualVideoId hala slug formatındaysa, manuel olarak YouTube ID'yi çıkar
  let finalVideoId = actualVideoId;
  
  // Herhangi bir değerde --- varsa YouTube ID'yi çıkar
  if (actualVideoId.includes('---')) {
    const parts = actualVideoId.split('---');
    
    // Son parçadan başlayarak 11 karakterlik YouTube ID'yi bul
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i];
      
      if (part && part.length === 11 && /^[a-zA-Z0-9_-]+$/.test(part)) {
        finalVideoId = part;
        break;
      }
    }
  }
  
  if (!finalVideoId) {
    return;
  }

  // PUBLIC teaser endpoint kullan
  try {
    const vd = await api.get(`/api/public/video/${finalVideoId}/${lang.value}`);
    
    pageTitle.value = vd?.title || 'Video Özeti';
    shortDescription.value = vd?.description || '';
    publishedAt.value = vd?.published_at || '';
    channelId.value = vd?.channel_id || '';
    channelTitle.value = vd?.channel_title || '';
    availableLanguages.value = vd?.available_languages || [];
    thumbnailUrl.value = vd?.thumbnail_url || '';
    
    // URL'yi SEO-friendly yap
    updateUrlToSeoFriendly(finalVideoId, pageTitle.value);
  } catch (error) {
    // Silent error handling
  }

  // 2) Summary çek
  try {
    const s = await api.get(`/api/public/video/${finalVideoId}/${lang.value}`);
    const summary_excerpt = s?.summary_excerpt || '';
    summaryHtml.value = summary_excerpt ? `<p>${summary_excerpt}</p>` : '';
  } catch (error) {
    // Silent error handling
  }

  // TTS (public'te göstermiyoruz)

  // Meta güncelle
  const seoTitle = `${pageTitle.value} — ${lang.value.toUpperCase()} Özet | Veciz AI`;
  const seoDescription = shortDescription.value || `${pageTitle.value} için ${lang.value.toUpperCase()} kısa özet. Veciz AI ile YouTube video özetleme.`;
  
  setMeta(seoTitle, seoDescription);
  updateSeo();
  
  // Structured Data (JSON-LD) ekle
  addStructuredData();
}

onMounted(fetchData);
watch(() => route.fullPath, () => {
  videoId.value = route.params.videoId as string;
  slug.value = route.params.slug as string;
  lang.value = (route.params.lang as string) || 'tr';
  fetchData();
});
</script>

<style scoped>
.container { max-width: 800px; }
</style>


